// apps/share-web/src/player-view/words-only-mount.ts
//
// Песня БЕЗ записи внутри плеера (единый сетлист, фаза 3, design gate G2).
// Рельса выбрала строку без shareId (setlist-songs-rail.ts) — плеер не может
// открыть трек, которого нет, но и не имеет права молчать: главная область
// показывает ту же шапку песни, что и у песни с записью (`<bw-song-header>` —
// mount.ts строит её так же, см. song-header.ts), пунктирный чип «No
// recording — words only» и лист чтения слов НА ВСЮ ШИРИНУ внутри панели того
// же ранга, что и раздвижная Chart & Lyrics (`.cw-panel`, chart.css) — без
// транспорта (играть тут нечем — ни трека, ни аудио).
//
// Разметка — `.pv-root`/`.pv-header`/`.pv-songhead`, те же классы, что строит
// `mountPlayerView` (mount.ts) для песни С записью: это ДРУГОЕ содержимое
// того же `#share-player-panel`, не отдельная мини-страница со своей шапкой —
// критика живой приёмки (16073e7c, пункт 2) была ровно в том, что первая
// редакция рисовала голый текст без материала плеера.
//
// «Не переизобретать рендер аккордов» — буквально: `renderChart`
// (chart/chart-render.ts) — единственный узел, который рисует аккорды/лирику
// из текста чарта; ни одна строка этой математики здесь не продублирована.
// Полный `chart-fullscreen.ts`/слой чтения — НЕ подошёл напрямую: его `.cw-fs`
// жёстко `position: fixed; inset: 0` (chart.css:401) — это НАМЕРЕННО, слой
// создан быть модальным полноэкранным чтением (страница слов, кнопка «Chart &
// Lyrics»), и смонтировать его сюда означало бы закрыть им рельсу, а бриф
// прямо требует «rail stays». Поэтому здесь `renderChart` стоит в `.cw-pane`
// внутри `.wo-panel` — панель СРЕДНЕГО яруса (`--panel-mid`), тот же материал,
// что несёт `.cw-panel` (раздвижная панель) и `.pv-transport` (chart.css/
// player-view.css оба зовут её «карточка того же ранга»), без кнопок
// настроек/полноэкранного/навигации по сетлисту — те остаются привилегией
// слоя, который открывается по кнопке «Chart & Lyrics» и здесь тоже доступен
// (эта поверхность его не заменяет, а стоит ДО него).
import '@bandway/ui-components';
import '@bandway/ui-components/styles/bw-song-header.css';
import { renderChart } from '../chart/chart-render.js';
import { t } from '../ui/i18n.js';
import '../styles/chart.css';
import './player-view.css';
import '../styles/words-only.css';

export interface WordsOnlySong {
  name: string;
  key?: string;
  bpm?: number;
  /** Текст чарта (ChordPro) — пустая строка/undefined → «слов пока нет» (renderChart сам это рисует). */
  text: string;
}

export interface WordsOnlyMountHandle {
  unmount(): void;
}

export function mountWordsOnly(host: Element, song: WordsOnlySong): WordsOnlyMountHandle {
  host.textContent = '';
  // Телефонный экран песни не опускает флаг при размонтаже (смена песни не должна вспыхивать
  // каркасом). Этому экрану шапка и рельса нужны для навигации — флаг опускает он.
  document.body.removeAttribute('data-pv-phone');

  const root = document.createElement('div');
  root.className = 'pv-root wo-root';
  root.dataset.role = 'words-only';

  // Та же шапка, что у песни С записью (song-header.ts): имя + тональность/
  // темп чипами. `no-song-label` не нужен — имя у песни без записи есть
  // всегда, отсутствует ровно запись, не сама песня.
  const header = document.createElement('div');
  header.className = 'pv-header';
  const songhead = document.createElement('div');
  songhead.className = 'pv-songhead';
  const songHeaderEl = document.createElement('bw-song-header') as HTMLElement & {
    song: { name?: string; key?: string; bpm?: number };
    compact: boolean;
  };
  songHeaderEl.song = { name: song.name || t('library.untitled'), key: song.key, bpm: song.bpm };
  songHeaderEl.compact = true;
  songhead.appendChild(songHeaderEl);
  header.appendChild(songhead);
  root.appendChild(header);

  // Пунктирный чип — тот же язык, что у пустых слотов ряда песни (library.css
  // `.lib-slot.is-absent`): рамка --fg-4 пунктиром, не заливка, не «поблёкшая
  // кнопка» — здесь не кнопка вовсе, только называет состояние поверхности.
  const chip = document.createElement('div');
  chip.className = 'wo-chip';
  chip.dataset.role = 'words-only-chip';
  chip.textContent = t('wordsOnly.chip');
  root.appendChild(chip);

  // Панель чтения — материал среднего яруса (см. шапку модуля), не голый текст
  // на шасси. Внутри — ровно тот же `.cw-pane`/`.cw-body`, что несёт панель
  // Chart & Lyrics и слой чтения; chart.css уже стилизует их типографику и
  // цвет роли аккорда (`--chord`) без единой строки здесь.
  const panel = document.createElement('div');
  panel.className = 'wo-panel';
  const body = document.createElement('div');
  body.className = 'wo-body cw-body';
  const pane = document.createElement('div');
  pane.className = 'cw-pane wo-pane';
  // Тот же контракт, что у слоя чтения (chart-panel.ts/chart-fullscreen.ts):
  // renderChart на пустом тексте не рисует НИЧЕГО («ни листа, ни рамки» —
  // его собственный комментарий), поэтому «слов пока нет» — дело вызывающего,
  // а не молчаливая пустота.
  const hasWords = (song.text ?? '').trim().length > 0;
  if (hasWords) {
    renderChart(pane, { text: song.text, shift: 0, columns: 1 });
  } else {
    const empty = document.createElement('div');
    empty.className = 'cw-empty';
    empty.textContent = t('chart.empty');
    pane.appendChild(empty);
  }
  body.appendChild(pane);
  panel.appendChild(body);
  root.appendChild(panel);

  host.appendChild(root);

  return {
    unmount(): void { host.textContent = ''; },
  };
}
